import React, { useState, useEffect } from 'react';
import { PlusIcon, PhotoIcon, TrashIcon, PencilSquareIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { api } from '../../services/api';
import { FormField, Input, TextArea, Select } from '../../components/common/FormField';
import { Modal } from '../../components/common/Modal';
import { ImageUploadService } from '../../services/imageUpload';
import { useSnackbar } from 'notistack';

type Category = 'LIVERY' | 'LOGO' | 'TSHIRT' | 'CUSTOM';

interface PortfolioItem {
    id: string;
    title: string;
    category: Category;
    description: string;
    images: string[];
    metadata?: Record<string, any>;
    createdAt: string;
    updatedAt: string;
}

interface PortfolioFormData {
    title: string;
    category: Category;
    description: string;
    images: string[];
}

interface UploadStatus {
    progress: number;
    error: string | null;
    retryCount: number;
}

const initialFormData: PortfolioFormData = {
    title: '',
    category: 'LIVERY',
    description: '',
    images: []
};

const PortfolioFormContent: React.FC<{
    formData: PortfolioFormData;
    setFormData: React.Dispatch<React.SetStateAction<PortfolioFormData>>;
    handleSubmit: (e: React.FormEvent) => Promise<void>;
    handleImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
    handleRemoveImage: (index: number) => void;
    uploadStatus: UploadStatus;
    editingItem: PortfolioItem | null;
    onClose: () => void;
}> = ({ 
    formData, 
    setFormData, 
    handleSubmit, 
    handleImageUpload, 
    handleRemoveImage,
    uploadStatus,
    editingItem,
    onClose 
}) => {
    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <FormField label="Title" required>
                <Input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="w-full bg-black/30 border-gray-600 text-gray-200"
                />
            </FormField>

            <FormField label="Category" required>
                <Select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value as Category })}
                    className="w-full bg-black/30 border-gray-600 text-gray-200"
                    required
                    options={[
                        { value: 'LIVERY', label: 'Racing Livery' },
                        { value: 'LOGO', label: 'Logo Design' },
                        { value: 'TSHIRT', label: 'T-Shirt Design' },
                        { value: 'CUSTOM', label: 'Custom Graphics' }
                    ]}
                />
            </FormField>

            <FormField label="Description" required>
                <TextArea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={4}
                    className="w-full bg-black/30 border-gray-600 text-gray-200"
                    required
                />
            </FormField>

            <div className="space-y-2">
                <label className="block text-sm font-medium text-teal-300">Images</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {formData.images.map((image, index) => (
                        <div key={index} className="relative aspect-square bg-black/30 rounded-lg overflow-hidden">
                            <img
                                src={ImageUploadService.getImageUrl(image)}
                                alt={`Portfolio item ${index + 1}`}
                                className="w-full h-full object-cover"
                            />
                            <button
                                type="button"
                                onClick={() => handleRemoveImage(index)}
                                className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                            >
                                <XMarkIcon className="h-4 w-4" />
                            </button>
                        </div>
                    ))}
                    <div className="aspect-square bg-black/30 border-2 border-dashed border-gray-600 rounded-lg flex items-center justify-center relative">
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            className="hidden"
                            id="image-upload"
                            multiple
                        />
                        <label
                            htmlFor="image-upload"
                            className="cursor-pointer text-center p-4 w-full h-full flex flex-col items-center justify-center"
                        >
                            {uploadStatus.progress > 0 && uploadStatus.progress < 100 ? (
                                <div className="space-y-2">
                                    <div className="w-16 h-16 rounded-full border-4 border-teal-500/20 border-t-teal-500 animate-spin" />
                                    <span className="block text-sm text-teal-300">
                                        {uploadStatus.progress}%
                                    </span>
                                </div>
                            ) : (
                                <>
                                    <PhotoIcon className="h-8 w-8 text-gray-400" />
                                    <span className="mt-2 block text-sm text-gray-400">
                                        Add Images
                                    </span>
                                </>
                            )}
                        </label>
                    </div>
                </div>
                {uploadStatus.error && (
                    <div className="mt-2 p-3 bg-red-900/30 border border-red-500/30 rounded-md">
                        <p className="text-sm text-red-400">{uploadStatus.error}</p>
                        <p className="text-xs text-gray-400 mt-1">
                            Retry attempt {uploadStatus.retryCount} of 3
                        </p>
                    </div>
                )}
            </div>

            <div className="flex justify-end gap-3 pt-6 border-t border-gray-600">
                <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-gray-100"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={uploadStatus.progress > 0 && uploadStatus.progress < 100}
                    className="px-4 py-2 bg-teal-500 text-white rounded-md hover:bg-teal-600 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {editingItem ? 'Update Item' : 'Create Item'}
                </button>
            </div>
        </form>
    );
};

export const PortfolioManagementPage: React.FC = () => {
    const [items, setItems] = useState<PortfolioItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<PortfolioItem | null>(null);
    const [uploadStatus, setUploadStatus] = useState<UploadStatus>({
        progress: 0,
        error: null,
        retryCount: 0
    });
    const [formData, setFormData] = useState<PortfolioFormData>(initialFormData);
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        fetchItems();
    }, []);

    const fetchItems = async () => {
        try {
            const data = await api.portfolio.getItems();
            setItems(data);
            setError(null);
        } catch (err) {
            setError('Failed to load portfolio items');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files?.length) return;
        
        setUploadStatus({ progress: 0, error: null, retryCount: 0 });

        try {
            // Validate all files first
            const files = Array.from(e.target.files);
            const { valid, errors } = await ImageUploadService.validateImages(files);

            if (errors.length > 0) {
                const errorMessages = errors.map(e => `${e.file.name}: ${e.error}`).join('\n');
                setUploadStatus(prev => ({ ...prev, error: errorMessages }));
                return;
            }

            // Upload all valid files
            const urls = await ImageUploadService.uploadImage(valid, 'portfolio', {
                onProgress: (progress) => {
                    setUploadStatus(prev => ({ ...prev, progress: progress.percentage }));
                },
                maxRetries: 3,
                retryDelay: 1000
            });
            
            setFormData(prev => ({
                ...prev,
                images: [...prev.images, ...(Array.isArray(urls) ? urls : [urls])],
            }));

            // Clear the input and status
            e.target.value = '';
            setUploadStatus({ progress: 0, error: null, retryCount: 0 });
            enqueueSnackbar('Images uploaded successfully', { variant: 'success' });
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to upload images';
            setUploadStatus(prev => ({
                progress: 0,
                error: errorMessage,
                retryCount: prev.retryCount + 1
            }));
            enqueueSnackbar(errorMessage, { variant: 'error' });
        }
    };

    const handleRemoveImage = (index: number) => {
        setFormData(prev => ({
            ...prev,
            images: prev.images.filter((_, i) => i !== index),
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (editingItem) {
                await api.portfolio.updateItem(editingItem.id, formData);
                enqueueSnackbar('Portfolio item updated successfully', { variant: 'success' });
            } else {
                await api.portfolio.createItem(formData);
                enqueueSnackbar('Portfolio item created successfully', { variant: 'success' });
            }
            await fetchItems();
            setIsModalOpen(false);
            resetForm();
        } catch (err) {
            console.error('Failed to save portfolio item:', err);
            enqueueSnackbar('Failed to save portfolio item', { variant: 'error' });
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this item?')) return;

        try {
            await api.portfolio.deleteItem(id);
            await fetchItems();
            enqueueSnackbar('Portfolio item deleted successfully', { variant: 'success' });
        } catch (err) {
            console.error('Failed to delete item:', err);
            enqueueSnackbar('Failed to delete portfolio item', { variant: 'error' });
        }
    };

    const openEditModal = (item: PortfolioItem) => {
        setEditingItem(item);
        setFormData({
            title: item.title,
            description: item.description,
            category: item.category,
            images: item.images,
        });
        setIsModalOpen(true);
    };

    const resetForm = () => {
        setEditingItem(null);
        setFormData(initialFormData);
        setUploadStatus({ progress: 0, error: null, retryCount: 0 });
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h1 className="text-2xl font-semibold text-teal-300">Portfolio</h1>
                <button
                    onClick={() => {
                        resetForm();
                        setIsModalOpen(true);
                    }}
                    className="inline-flex items-center px-4 py-2 bg-teal-500 text-white rounded-md hover:bg-teal-600 transition-colors"
                >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Add Item
                </button>
            </div>

            {isLoading ? (
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-400"></div>
                </div>
            ) : error ? (
                <div className="text-red-400 text-center py-8">
                    <p>{error}</p>
                    <button
                        onClick={fetchItems}
                        className="mt-4 px-4 py-2 bg-teal-500 text-white rounded hover:bg-teal-600"
                    >
                        Try Again
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {items.map((item) => (
                        <div
                            key={item.id}
                            className="bg-black/20 backdrop-blur-md rounded-lg border border-teal-500/20 overflow-hidden"
                        >
                            <div className="aspect-w-16 aspect-h-9 relative group">
                                {item.images[0] && (
                                    <img
                                        src={ImageUploadService.getImageUrl(item.images[0])}
                                        alt={item.title}
                                        className="w-full h-full object-cover"
                                    />
                                )}
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={() => openEditModal(item)}
                                            className="p-2 bg-teal-500 text-white rounded-full hover:bg-teal-600 transition-colors"
                                        >
                                            <PencilSquareIcon className="h-5 w-5" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(item.id)}
                                            className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                                        >
                                            <TrashIcon className="h-5 w-5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="p-4">
                                <div className="flex justify-between items-start mb-2">
                                    <h3 className="text-lg font-medium text-gray-200 truncate">{item.title}</h3>
                                    <span className="ml-2 px-2 py-1 text-xs font-semibold rounded-full bg-teal-900/50 text-teal-300 whitespace-nowrap">
                                        {item.category}
                                    </span>
                                </div>
                                <p className="text-sm text-gray-400 mb-4 line-clamp-2">{item.description}</p>
                                <div className="flex justify-between items-center">
                                    <span className="text-xs text-gray-500">
                                        {new Date(item.createdAt).toLocaleDateString()}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Modal 
                isOpen={isModalOpen} 
                onClose={() => setIsModalOpen(false)} 
                title={editingItem ? 'Edit Portfolio Item' : 'Add Portfolio Item'}
            >
                <PortfolioFormContent
                    formData={formData}
                    setFormData={setFormData}
                    handleSubmit={handleSubmit}
                    handleImageUpload={handleImageUpload}
                    handleRemoveImage={handleRemoveImage}
                    uploadStatus={uploadStatus}
                    editingItem={editingItem}
                    onClose={() => setIsModalOpen(false)}
                />
            </Modal>
        </div>
    );
}; 